/**
 * Gorev Sablonlari (Templates)
 *
 * Excel'deki ornek gorevler (Subat/Mart/Nisan 2026) ve analiz dokumaninda
 * netleshen 6 gorev tipinden turetildi. Amac: admin "Yeni Gorev" tiklayinca
 * bos bir form yerine, hazir onay degerleriyle dolmus bir template secsin ve
 * yalnizca farkli olan alanlari (gorev adi, puan eskisleri) girsin.
 *
 * Her template bir ``initialForm`` nesnesi uretir — TaskFormWizard'in
 * emptyForm'una benzer sekilde ancak template-spesifik dolu. Bu nesne
 * wizard'a ``initial={...}`` olarak gecirilir.
 *
 * ``locked_fields``: template gereksinimi olan alanlar (orn. HYO icin
 * metric_type='RATIO' ve exclude_filters sabit). Wizard bu alanlari
 * readonly/disabled gostermeli ki admin yanlislikla bozmasin.
 *
 * Kullanim akisi:
 *   Tasks/index.jsx → "Yeni Gorev" butonu → TaskTemplatePicker modal
 *     → admin secim yapar → TaskFormWizard acilir (initial = template.build())
 */

// ---- Dönem filtresi satırları (2026-09 AS kararı) ----
// Motor hiçbir AS tablosu için otomatik tarih filtresi uygulamaz; dönem görevin
// filtresinden gelir. Şablon dönem satırlarını BOŞ değerle getirir; wizard,
// Temel Bilgi'de görev tarihi (yoksa bağlı dönemin başlangıç/bitişi) seçilince
// değerleri otomatik doldurur (TaskFormWizard: taskDateRange / autoFilledRef).
// Admin isterse değeri elle değiştirir; elle değiştirilen değere dokunulmaz.
// Kural: uretim/prim/saglik/grup_saglik → (yil_no ve ay_no) veya uretim_tarihi;
//        yenileme → uretim_tarihi veya (yil_no ve ay_no); teklif → uretim_tarihi;
//        ziyaret → ziyaret_tarihi; hedefler → yilno ve ayno.

/** (yıl, ay) çifti — as_uretim: yil_no/ay_no · hedefler: yilno/ayno */
export const periodPairFilters = (yilCol, ayCol) => [
  { column: yilCol, op: '=', value: '' },
  { column: ayCol, op: '=', value: '' },
];

/** Tarih kolonuyla aralık: >= başlangıç, < bitişin ertesi günü (bitiş dahil) */
export const periodDateRangeFilters = (dateCol) => [
  { column: dateCol, op: '>=', value: '' },
  { column: dateCol, op: '<', value: '' },
];

export const TASK_TEMPLATES = [
  // ---------------------------------------------------------------------------
  // 0) OZEL (bos form) — ilk sirada gösterilir
  // ---------------------------------------------------------------------------
  {
    id: 'CUSTOM',
    label: 'Özel Görev (Boş Başla)',
    icon: 'plus',
    color: 'neutral',
    description:
      'Hiçbir ön-doldurma yok. Tüm alanları sıfırdan kendin gir. ' +
      'Yukarıdaki şablonlardan hiçbiri uymuyorsa kullan.',
    example: '',
    metric_label: '',
    locked_fields: [],
    build: () => null, // TaskFormWizard.emptyForm kullanilacak
  },

  // ---------------------------------------------------------------------------
  // 1) ADET GOREVI
  // ---------------------------------------------------------------------------
  {
    id: 'ADET',
    label: 'Adet Görevi',
    icon: 'tasks',
    color: 'blue',
    description:
      'Poliçe, sigortalı veya benzeri kayıt sayısı üzerinden puanlama. ' +
      'Kademeli bonus (≥ X adet → Y puan) yapısı hazır gelir.',
    example:
      'Örn. Kasko adet görevi, KOBİ TSS adet, Yurtdışı Seyahat adet.',
    metric_label: 'Adet',
    locked_fields: [], // admin her şeyi düzenleyebilir
    build: () => ({
      // 2026-09 AS aylık model: as_uretim satırları toplulaştırılmış gelir
      // (police_adedi = N) → adet = SUM(police_adedi). COUNT satır sayar ve
      // eksik sayardı; SUM satır-başına-1 veride de aynı sonucu verir.
      metric_type: 'SUM',
      source_table: 'as_uretim',
      metric_column: 'police_adedi',
      formula_numerator: '',
      formula_denominator: '',
      // Dönem filtresi ZORUNLU: as_uretim her ay (yil_no, ay_no) partition'ı
      // sil + yeniden yükle ile beslenir, motor otomatik tarih filtresi
      // uygulamaz. (Kural: yil_no ve ay_no VEYA uretim_tarihi.) Değerler wizard'da
      // görev tarihi / dönem seçilince otomatik dolar, elle değiştirilebilir.
      // Ardından Aktif poliçe filtresi — admin sistem sabiti picker'i ile
      // buna dokunabilir.
      filters: [
        ...periodPairFilters('yil_no', 'ay_no'),
        {
          column: 'police_durumu_adi',
          op: 'IN',
          value: '${AKTIF_POLICE_DURUMLARI}',
        },
      ],
      // Net uretim: aktif olmayan durumlari dus (iptal/fesih vb.)
      exclude_filters: [
        {
          column: 'police_durumu_adi',
          op: 'NOT IN',
          value: '${AKTIF_POLICE_DURUMLARI}',
        },
      ],
      max_points: 25,
      is_joker: false,
      cancellation_policy: 'CANCEL_REVOKE',
      tiers: [
        { threshold_operator: 'GTE', threshold_value: 20, points: 5, sort_order: 1 },
        { threshold_operator: 'GTE', threshold_value: 25, points: 10, sort_order: 2 },
        { threshold_operator: 'GTE', threshold_value: 30, points: 15, sort_order: 3 },
      ],
    }),
  },

  // ---------------------------------------------------------------------------
  // 2) HYO GOREVI
  // ---------------------------------------------------------------------------
  {
    id: 'HYO',
    label: 'HYO Görevi',
    icon: 'trophy',
    color: 'amber',
    description:
      'Hedefe Yaklaşma Oranı = (uretim_buyil_trafikdahil / hedef_trafikdahil) × 100. ' +
      'Admin görev adı, puan eşikleri ve max puan girer.',
    example:
      'Örn. Sorumlu Acente HYO (Şubat/Mart/Nisan — her dönemde aynı formül, farklı puanlama).',
    metric_label: 'Oran (HYO)',
    // Bu alanlar template gereksinimi — admin değiştiremez
    locked_fields: [
      'metric_type',
      'source_table',
      'formula_numerator',
      'formula_denominator',
      'exclude_filters',
    ],
    build: () => ({
      metric_type: 'RATIO',
      source_table: 'as_rota_hedefler',
      metric_column: '',
      formula_numerator: 'uretim_buyil_trafikdahil',
      formula_denominator: 'hedef_trafikdahil',
      // HYO = (uretim / hedef) × 100 → eşikler yüzde cinsinden (80/90/100/110).
      ratio_multiplier: 100,
      // Hedef tablosunda dönem = (yilno, ayno) — görevde ZORUNLU; motor artık
      // otomatik dönem filtresi uygulamaz. Değerler wizard'da dönem seçilince dolar.
      filters: [...periodPairFilters('yilno', 'ayno')],
      // HYO hesabinda 00-Toplam ve 01-Trafik branslari otomatik haric —
      // sistem sabiti ile referans alinir, admin sabit listesini
      // degistirince tum HYO gorevleri otomatik guncellenir.
      exclude_filters: [
        {
          column: 'branskodu_bransadi',
          op: 'IN',
          value: '${HYO_HARIC_BRANSLAR}',
        },
      ],
      max_points: 30,
      is_joker: false,
      cancellation_policy: 'CANCEL_REVOKE',
      // HYO yuzde tabanli eşikler (%80, %90, %100, %110)
      tiers: [
        { threshold_operator: 'GTE', threshold_value: 80,  points: 10, sort_order: 1 },
        { threshold_operator: 'GTE', threshold_value: 90,  points: 15, sort_order: 2 },
        { threshold_operator: 'GTE', threshold_value: 100, points: 20, sort_order: 3 },
        { threshold_operator: 'GTE', threshold_value: 110, points: 30, sort_order: 4 },
      ],
    }),
  },

  // ---------------------------------------------------------------------------
  // 3) YENILEME ORANI GOREVI
  // ---------------------------------------------------------------------------
  {
    id: 'YENILEME',
    label: 'Yenileme Oranı Görevi',
    icon: 'trophy',
    color: 'teal',
    description:
      'Yenileme Oranı = (yenilenmis_police_sayisi_acente / yenilenecek_police_sayisi) × 100. ' +
      'Veri kaynağı: as_yenileme tablosu (2026-05-11 itibariyle Üretim topic\'inden ayrıldı). ' +
      'Admin görev adı, müdürlük kısıtı ve puan eşikleri girer.',
    example:
      'Örn. Konut Yenileme Oranı (Mart), Yenileme Başarı Oranı (Nisan).',
    metric_label: 'Oran (Yenileme)',
    locked_fields: ['metric_type', 'source_table', 'formula_numerator', 'formula_denominator'],
    build: () => ({
      metric_type: 'RATIO',
      // 2026-05-11: AS yenileme verisini prod.uretim'den prod.yenileme'ye tasidi.
      // Bu alanlar artik as_yenileme tablosunda — as_uretim'den drop edildi.
      source_table: 'as_yenileme',
      metric_column: '',
      formula_numerator: 'yenilenmis_police_sayisi_acente',
      formula_denominator: 'yenilenecek_police_sayisi',
      // Yenileme oranı = (yenilenen / yenilenecek) × 100 → eşikler yüzde cinsinden.
      ratio_multiplier: 100,
      // Dönem filtresi ZORUNLU (motor otomatik tarih filtresi uygulamaz);
      // değerler wizard'da görev tarihi / dönem seçilince dolar.
      filters: [...periodDateRangeFilters('uretim_tarihi')],
      exclude_filters: [],
      max_points: 30,
      is_joker: false,
      cancellation_policy: 'CANCEL_REVOKE',
      tiers: [
        { threshold_operator: 'GTE', threshold_value: 60, points: 10, sort_order: 1 },
        { threshold_operator: 'GTE', threshold_value: 70, points: 15, sort_order: 2 },
        { threshold_operator: 'GTE', threshold_value: 80, points: 20, sort_order: 3 },
        { threshold_operator: 'GTE', threshold_value: 90, points: 25, sort_order: 4 },
        { threshold_operator: 'GTE', threshold_value: 100, points: 30, sort_order: 5 },
      ],
    }),
  },

  // ---------------------------------------------------------------------------
  // 4) TUTAR / PRIM GOREVI
  // ---------------------------------------------------------------------------
  {
    id: 'TUTAR',
    label: 'Tutar Görevi (Prim)',
    icon: 'trophy',
    color: 'green',
    description:
      'PRIM tablosundan VERGİ_ONCESİ_PRİM toplamı. Yalnızca Faal acenteler ' +
      '(Acente_Durumu=0) dikkate alınır; iptal edilen poliçeler düşer.',
    example: 'Örn. aylık toplam prim üretimi eşik tabanlı puanlama.',
    metric_label: 'Tutar (TL)',
    locked_fields: ['metric_type', 'source_table', 'metric_column'],
    build: () => ({
      metric_type: 'SUM',
      source_table: 'as_prim',
      metric_column: 'vergi_oncesi_prim',
      formula_numerator: '',
      formula_denominator: '',
      filters: [
        // Dönem filtresi ZORUNLU (kural: yil_no ve ay_no VEYA uretim_tarihi).
        // 2026-09-08'den itibaren AS yil_no/ay_no'yu üretimle aynı formatta
        // gönderiyor → aylık dönem çifti; değerler wizard'da dönem seçilince dolar.
        ...periodPairFilters('yil_no', 'ay_no'),
        {
          column: 'acente_durumu',
          op: '=',
          value: '${FAAL_ACENTE_DURUMU}',
        },
        {
          column: 'police_durumu_adi',
          op: 'IN',
          value: '${AKTIF_POLICE_DURUMLARI}',
        },
      ],
      exclude_filters: [
        {
          column: 'police_durumu_adi',
          op: 'NOT IN',
          value: '${AKTIF_POLICE_DURUMLARI}',
        },
      ],
      max_points: 25,
      is_joker: false,
      cancellation_policy: 'CANCEL_REVOKE',
      tiers: [
        { threshold_operator: 'GTE', threshold_value: 100000,  points: 10, sort_order: 1 },
        { threshold_operator: 'GTE', threshold_value: 250000,  points: 15, sort_order: 2 },
        { threshold_operator: 'GTE', threshold_value: 500000,  points: 20, sort_order: 3 },
        { threshold_operator: 'GTE', threshold_value: 1000000, points: 25, sort_order: 4 },
      ],
    }),
  },

  // ---------------------------------------------------------------------------
  // 5) SIGORTALI SAYISI GOREVI (Saglik)
  // ---------------------------------------------------------------------------
  {
    id: 'SIGORTALI',
    label: 'Sigortalı Sayısı Görevi',
    icon: 'users',
    color: 'sky',
    description:
      'SAĞLIK tablosundan SIGORTALI_SAYISI_KONTAK toplamı. Yaş grubu filtresi ' +
      'gerekirse sonradan eklenebilir.',
    example: 'Örn. Bireysel TSS (yeni sigortalı sayısı, Poliçe Kodu 956).',
    metric_label: 'Sigortalı Sayısı',
    locked_fields: ['metric_type', 'source_table', 'metric_column'],
    build: () => ({
      metric_type: 'SUM',
      source_table: 'as_saglik',
      metric_column: 'sigortali_sayisi_kontak',
      formula_numerator: '',
      formula_denominator: '',
      filters: [
        // Dönem filtresi ZORUNLU (kural: yil_no ve ay_no VEYA uretim_tarihi).
        // 2026-09-08'den itibaren AS yil_no/ay_no'yu üretimle aynı formatta
        // gönderiyor → aylık dönem çifti; değerler wizard'da dönem seçilince dolar.
        ...periodPairFilters('yil_no', 'ay_no'),
        {
          column: 'police_durumu_adi',
          op: 'IN',
          value: '${AKTIF_POLICE_DURUMLARI}',
        },
      ],
      exclude_filters: [
        {
          column: 'police_durumu_adi',
          op: 'NOT IN',
          value: '${AKTIF_POLICE_DURUMLARI}',
        },
      ],
      max_points: 15,
      is_joker: false,
      cancellation_policy: 'CANCEL_REVOKE',
      tiers: [
        { threshold_operator: 'GTE', threshold_value: 5,  points: 3, sort_order: 1 },
        { threshold_operator: 'GTE', threshold_value: 10, points: 6, sort_order: 2 },
        { threshold_operator: 'GTE', threshold_value: 15, points: 9, sort_order: 3 },
        { threshold_operator: 'GTE', threshold_value: 20, points: 12, sort_order: 4 },
        { threshold_operator: 'GTE', threshold_value: 25, points: 15, sort_order: 5 },
      ],
    }),
  },

  // ---------------------------------------------------------------------------
  // 6) JOKER / EK GOREV (Ranking)
  // ---------------------------------------------------------------------------
  {
    id: 'JOKER',
    label: 'Joker (Ek) Görev',
    icon: 'trophy',
    color: 'pink',
    description:
      'Müdürlük bazında sıralamada ilk 3\'e sabit puan verir. Klasik kademeli ' +
      'bonus yerine sıralama ödülü uygular (örn. 1.→15, 2.→10, 3.→5 puan).',
    example:
      'Örn. KOBİ Satış Fırsatları (Mart), KÖP Katılım Oranı (Nisan).',
    metric_label: 'Sıralama (RANK)',
    locked_fields: ['metric_type', 'is_joker', 'cancellation_policy'],
    build: () => ({
      metric_type: 'RANK',
      source_table: 'as_teklif',
      metric_column: 'teklif_sayisi',
      formula_numerator: '',
      formula_denominator: '',
      // Dönem filtresi ZORUNLU (motor otomatik tarih filtresi uygulamaz);
      // değerler wizard'da görev tarihi / dönem seçilince dolar.
      filters: [...periodDateRangeFilters('uretim_tarihi')],
      exclude_filters: [],
      max_points: 15,
      is_joker: true,
      cancellation_policy: 'FULL_POINTS', // joker iptal edilmez, top-3 puan kalir
      // Joker puanlamasi: rank tabanli sabit puan (1.=15, 2.=10, 3.=5)
      // threshold_value rank'i, points verilecek puani temsil eder.
      tiers: [
        { threshold_operator: 'EQ', threshold_value: 1, points: 15, sort_order: 1 },
        { threshold_operator: 'EQ', threshold_value: 2, points: 10, sort_order: 2 },
        { threshold_operator: 'EQ', threshold_value: 3, points: 5,  sort_order: 3 },
      ],
    }),
  },

  // ---------------------------------------------------------------------------
  // 7) MANUEL GOREV
  // ---------------------------------------------------------------------------
  {
    id: 'MANUAL',
    label: 'Manuel Görev',
    icon: 'edit',
    color: 'neutral',
    description:
      'Otomatik hesaplama yok. Admin her kullanıcı için elle puan girer ' +
      '(toplu Excel yükleme veya tek tek giriş).',
    example: 'Örn. özel bir etkinlik veya takdir puanı.',
    metric_label: 'Manuel',
    locked_fields: ['metric_type', 'source_table'],
    build: () => ({
      metric_type: 'MANUAL',
      source_table: '',
      metric_column: '',
      formula_numerator: '',
      formula_denominator: '',
      filters: [],
      exclude_filters: [],
      max_points: 20,
      is_joker: false,
      cancellation_policy: 'FULL_POINTS',
      tiers: [], // manual'da tier yok
    }),
  },

];

/**
 * Template id → template object.
 */
export function getTemplate(id) {
  return TASK_TEMPLATES.find((t) => t.id === id) || null;
}
